import { useState, useRef, useEffect, useMemo } from 'react';
import { toast } from '@/lib/toast';
import { contactFormSchema } from '@/lib/validationSchemas';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, ArrowUp, Check, Loader2, Paperclip, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { processSelectedFiles, resetFileInput, IMAGE_ACCEPT } from '@/lib/photoInputUtils';
import teamAvatar from '@/assets/cardsimp-team-avatar.png';


type Subject = { value: string; label: string; emoji: string; echo: string; reply: string };

const SUBJECTS: Subject[] = [
	{
		value: 'Question / Support',
		label: 'Support',
		emoji: '💬',
		echo: 'I need some help.',
		reply:
			"We're here to help. Please describe your question or issue below — we usually reply within 24–48 hours. Include screenshots and your CardSimp handle if you have one.",
	},
	{
		value: 'Feedback',
		label: 'Feedback',
		emoji: '✨',
		echo: 'I have feedback.',
		reply:
			"Your input shapes what we build next. Let us know what you want to see.",
	},
	{
		value: 'Bug Report',
		label: 'Bug',
		emoji: '🐛',
		echo: 'I found a bug.',
		reply:
			"Sorry about that. Please include what you were doing, what happened instead, and your device/browser. Screenshots help a ton.",
	},
	{
		value: 'Feature Request',
		label: 'Feature idea',
		emoji: '💡',
		echo: 'I have a feature idea.',
		reply:
			"Describe an idea and the problem it would solve for you.",
	},
	{
		value: 'Partnership',
		label: 'Partnership',
		emoji: '🤝',
		echo: "Let's partner up.",
		reply:
			"Send a brief note of what you're imagining, and we'll contact you.",
	},
	{
		value: 'Business',
		label: 'Business',
		emoji: '📈',
		echo: 'Business inquiry.',
		reply:
			"For vendor, press, or commercial inquiries, send a brief note and we'll route you to the right contact.",
	},
];

const TeamAvatar = ({ size = 44 }: { size?: number }) => (
	<div
		className="relative shrink-0 rounded-full overflow-hidden bg-[hsl(var(--active-gold)/0.15)]"
		style={{
			width: size,
			height: size,
			boxShadow: '0 0 0 1px hsl(var(--active-gold) / 0.25)',
		}}
	>
		<img
			src={teamAvatar}
			alt="CardSimp Team"
			className="absolute inset-0 w-full h-full object-cover scale-[1.35] translate-y-[6%]"
			draggable={false}
		/>
	</div>
);

const greetingFor = () => 'Hello.';

const SendButton = ({
	canSend,
	isSubmitting,
	pct,
}: {
	canSend: boolean;
	isSubmitting: boolean;
	pct: number;
}) => {
	const r = 18;
	const c = 2 * Math.PI * r;
	return (
		<button
			type="submit"
			disabled={!canSend}
			aria-label="Send message"
			className={cn(
				'relative h-10 w-10 shrink-0 rounded-full grid place-items-center transition-all',
				canSend
					? 'bg-primary text-primary-foreground active:scale-90 shadow-md shadow-primary/30'
					: 'bg-white/[0.08] text-muted-foreground/60'
			)}
		>
			{pct >= 0.8 && (
				<svg className="absolute inset-0 -rotate-90" viewBox="0 0 40 40">
					<circle cx="20" cy="20" r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2" />
					<circle
						cx="20"
						cy="20"
						r={r}
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="butt"
						strokeDasharray={c}
						strokeDashoffset={c * (1 - pct)}
						className="transition-all"
					/>
				</svg>
			)}
			{isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" strokeWidth={2.5} />}
		</button>
	);
};

interface ContactMessagerProps {
	initialSubject?: string;
	className?: string;
	/** Optional header content rendered above the conversation (e.g. close/minimize chrome) */
	headerSlot?: React.ReactNode;
}

/**
 * Self-contained chat-style contact form.
 * Used both on the mobile /contact page and inside the desktop floating panel.
 */
export const ContactMessager = ({ initialSubject = '', className, headerSlot }: ContactMessagerProps) => {
	const orderedSubjects = useMemo(() => {
		if (!initialSubject) return SUBJECTS;
		const leadGroup =
			initialSubject === 'Business' || initialSubject === 'Partnership'
				? ['Business', 'Partnership']
				: [initialSubject];
		const lead = leadGroup
			.map((v) => SUBJECTS.find((s) => s.value === v))
			.filter((s): s is Subject => Boolean(s));
		const rest = SUBJECTS.filter((s) => !leadGroup.includes(s.value));
		return [...lead, ...rest];
	}, [initialSubject]);

	const [formData, setFormData] = useState({
		name: '',
		email: '',
		subject: initialSubject,
		message: '',
	});
	const [honeypot, setHoneypot] = useState('');
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [autoReplyTyping, setAutoReplyTyping] = useState(false);
	const [autoReplyShown, setAutoReplyShown] = useState<string | null>(null);
	const [echoShown, setEchoShown] = useState<string | null>(null);
	const [sentMessages, setSentMessages] = useState<string[]>([]);
	const [attachments, setAttachments] = useState<File[]>([]);
	
	const messageRef = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);

	const ATTACH_SUBJECTS = ['Bug Report', 'Feedback', 'Question / Support'];
	const allowsAttachments = ATTACH_SUBJECTS.includes(formData.subject);
	const MAX_ATTACHMENTS = 3;
	const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

	// React when parent opens with a new subject
	useEffect(() => {
		if (initialSubject && initialSubject !== formData.subject) {
			setFormData((p) => ({ ...p, subject: initialSubject }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialSubject]);

	useEffect(() => {
		if (!allowsAttachments && attachments.length > 0) setAttachments([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allowsAttachments]);

	const addAttachments = async (files: FileList | null, inputEl?: HTMLInputElement | null) => {
		const slots = MAX_ATTACHMENTS - attachments.length;
		if (slots <= 0) {
			toast.error(`Up to ${MAX_ATTACHMENTS} screenshots`);
			resetFileInput(inputEl);
			return;
		}
		const processed = await processSelectedFiles(files, { maxCount: slots, multiple: true });
		const sized = processed.filter((f) => {
			if (f.size > MAX_ATTACHMENT_BYTES) {
				toast.error(`${f.name} is over 5MB`);
				return false;
			}
			return true;
		});
		if (sized.length > 0) setAttachments((prev) => [...prev, ...sized]);
		resetFileInput(inputEl);
	};

	const removeAttachment = (i: number) =>
		setAttachments((prev) => prev.filter((_, idx) => idx !== i));

	const greeting = useMemo(() => greetingFor(), []);
	const activeSubject = useMemo(
		() => SUBJECTS.find((s) => s.value === formData.subject),
		[formData.subject]
	);

	// Auto-grow textarea
	useEffect(() => {
		const ta = messageRef.current;
		if (!ta) return;
		ta.style.height = 'auto';
		ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
	}, [formData.message]);

	// Canned auto-reply when a subject is picked
	useEffect(() => {
		if (!formData.subject) {
			setAutoReplyTyping(false);
			setAutoReplyShown(null);
			setEchoShown(null);
			return;
		}
		if (autoReplyShown === formData.subject) return;
		setAutoReplyShown(null);
		setEchoShown(null);
		setAutoReplyTyping(false);
		// 1. user echo appears
		const tEcho = setTimeout(() => setEchoShown(formData.subject), 220);
		// 2. typing indicator after echo settles
		const tType = setTimeout(() => setAutoReplyTyping(true), 720);
		// 3. reply lands
		const tReply = setTimeout(() => {
			setAutoReplyTyping(false);
			setAutoReplyShown(formData.subject);
		}, 1500);
		return () => {
			clearTimeout(tEcho);
			clearTimeout(tType);
			clearTimeout(tReply);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formData.subject]);

	// Auto-scroll to bottom when conversation grows
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
	}, [autoReplyShown, autoReplyTyping, echoShown, submitSuccess, formData.subject, sentMessages]);

	const update = (k: string, v: string) => {
		setFormData((p) => ({ ...p, [k]: v }));
		if (errors[k]) setErrors((p) => ({ ...p, [k]: '' }));
	};

	const handleEmailBlur = () => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (formData.email && !re.test(formData.email)) {
			setErrors((p) => ({ ...p, email: 'Please enter a valid email address' }));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();



		setErrors({});
		setIsSubmitting(true);
		try {
			if (honeypot !== '') {
				toast.success('Message sent successfully!', {
					description: "We'll get back to you within 24-48 hours.",
				});
				setIsSubmitting(false);
				return;
			}
			contactFormSchema.parse(formData);

			let attachmentPaths: string[] = [];
			if (allowsAttachments && attachments.length > 0) {
				const submissionId = crypto.randomUUID();
				const { resizeImageIfImage } = await import('@/lib/imageResizeUtils');
				const uploads = await Promise.all(
					attachments.map(async (file, idx) => {
						const compressed = await resizeImageIfImage(file);
						const safeName = compressed.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
						const path = `${submissionId}/${idx}-${safeName}`;
						const { error: upErr } = await supabase.storage
							.from('contact-attachments')
							.upload(path, compressed, {
								contentType: compressed.type || 'image/jpeg',
								upsert: false,
							});
						if (upErr) {
							console.error('Attachment upload failed:', upErr);
							return null;
						}
						return path;
					})
				);
				attachmentPaths = uploads.filter((p): p is string => p !== null);
				if (attachmentPaths.length < attachments.length) {
					toast.warning('Some screenshots failed to upload', {
						description: 'Sending the rest with your message.',
					});
				}
			}

			const { data: { user } } = await supabase.auth.getUser();
			const { error } = await supabase.functions.invoke('send-contact-email', {
				body: {
					...formData,
					website: honeypot,
					attachmentPaths,
					pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
					userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
					userId: user?.id ?? null,
				},
			});
			if (error) {
				if (error.message?.includes('429') || (error as any).context?.status === 429) {
					const resetAt = (error as any).context?.body?.resetAt;
					if (resetAt) {
						const mins = Math.ceil((new Date(resetAt).getTime() - Date.now()) / 60000);
						toast.error('Too many submissions', {
							description: `Please try again in ${mins} minute${mins !== 1 ? 's' : ''}.`,
						});
					} else {
						toast.error('Too many submissions', { description: 'Please try again later.' });
					}
					setIsSubmitting(false);
					return;
				}
				throw error;
			}
			setSentMessages((prev) => [...prev, formData.message]);
			setSubmitSuccess(true);
			toast.success('Message sent', {
				description: "We'll get back to you within 24–48 hours.",
			});
			setFormData({ name: '', email: '', subject: '', message: '' });
			setAttachments([]);
		} catch (err: any) {
			if (err.errors) {
				const ne: Record<string, string> = {};
				err.errors.forEach((e: any) => {
					if (e.path[0]) ne[e.path[0]] = e.message;
				});
				setErrors(ne);
				toast.error('Please check the form', { description: 'A few fields need attention.' });
			} else {
				toast.error('Could not send', { description: 'Please try again in a moment.' });
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const charPct = Math.min(formData.message.length / 2000, 1);
	const canSend =
		!isSubmitting &&
		formData.name.trim().length > 0 &&
		formData.email.trim().length > 0 &&
		formData.subject.length > 0 &&
		formData.message.trim().length > 0;

	return (
		<div className={cn('flex flex-col h-full bg-background', className)}>
			{/* Identity header */}
			<div className="px-4 pt-3 pb-2 shrink-0">
					<div className="flex items-center gap-3">
						<div className="relative rounded-full">
							<TeamAvatar size={40} />
							<span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success ring-2 ring-background" />
						</div>
					<div className="flex-1 min-w-0">
						<div className="text-[14px] font-semibold text-foreground leading-tight">
							CardSimp Team
						</div>
						<div className="text-[11px] text-muted-foreground leading-tight truncate">
							contact@cardsimp.com
						</div>
					</div>
					{headerSlot}
				</div>
			</div>

			<div className="h-px bg-white/[0.06] mx-4" />

			{/* Conversation */}
			<div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-3 space-y-3">
				<div className="flex items-center gap-2 animate-fade-in">
					<TeamAvatar size={24} />
					<div className="max-w-[82%] rounded-2xl rounded-bl-md bg-white/[0.05] px-4 py-2.5 text-[14px] text-foreground leading-snug">
						{greeting} How can we help you today?
					</div>
				</div>


				{echoShown && activeSubject && (
					<div className="flex items-center gap-2 justify-end animate-fade-in pt-1">
						<div className="max-w-[85%] rounded-2xl rounded-br-md bg-white/[0.08] px-4 py-2.5 text-[14px] text-foreground leading-snug">
							{activeSubject.echo}
						</div>
						<div
							className="h-6 w-6 shrink-0 rounded-full bg-white/[0.08] border border-white/[0.10] grid place-items-center"
							aria-hidden
						>
							<svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-foreground/50" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<circle cx="12" cy="8" r="4" />
								<path d="M5 21v-1a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v1" />
							</svg>
						</div>
					</div>
				)}

				{autoReplyTyping && (
					<div className="flex items-center gap-2 animate-fade-in pt-1">
						<TeamAvatar size={24} />
						<div className="rounded-2xl rounded-bl-md bg-white/[0.05] px-4 py-3 inline-flex items-center gap-1">
							<span className="h-1.5 w-1.5 rounded-full bg-foreground/50 animate-[contact-typing_1s_infinite]" />
							<span className="h-1.5 w-1.5 rounded-full bg-foreground/50 animate-[contact-typing_1s_infinite] [animation-delay:150ms]" />
							<span className="h-1.5 w-1.5 rounded-full bg-foreground/50 animate-[contact-typing_1s_infinite] [animation-delay:300ms]" />
						</div>
					</div>
				)}
				{!autoReplyTyping && autoReplyShown && activeSubject && (
					<div className="flex items-center gap-2 animate-fade-in pt-1">
						<TeamAvatar size={24} />
						<div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white/[0.05] px-4 py-2.5 text-[14px] text-foreground leading-snug">
							{activeSubject.reply}
						</div>
					</div>
				)}


				{sentMessages.map((m, i) => (
					<div key={i} className="flex justify-end animate-fade-in pt-1">
						<div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary text-primary-foreground px-4 py-2.5 text-[14px] leading-snug whitespace-pre-wrap break-words shadow-md shadow-primary/20">
							{m}
						</div>
					</div>
				))}

				{submitSuccess && (
					<div className="flex items-end gap-2 animate-fade-in pt-1">
						<TeamAvatar size={24} />
						<div className="max-w-[82%] rounded-2xl rounded-bl-md bg-white/[0.05] px-4 py-2.5 text-[14px] text-foreground leading-snug">
							Got it ✨ We'll reply within 24–48 hours.{' '}
							<button
								type="button"
								onClick={() => setSubmitSuccess(false)}
								className="text-primary font-medium underline-offset-2 hover:underline"
							>
								Send another
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Subject pills */}
			<div className="px-3 pt-2 pb-1 shrink-0 border-t border-white/[0.04]">
				<div>
					<div className="flex flex-wrap gap-2">
						{formData.subject && (
							<button
								type="button"
								onClick={() => update('subject', '')}
								aria-label="Back to topics"
								className="inline-flex items-center gap-1 h-8 pl-2 pr-3 rounded-full text-[12.5px] font-medium transition-all whitespace-nowrap active:scale-95 border bg-white/[0.05] border-white/[0.08] text-foreground hover:bg-white/[0.08]"
							>
								<ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
								Back
							</button>
						)}
						{orderedSubjects
							.filter((s) => !formData.subject || s.value === formData.subject)
							.map((s) => {
								const active = formData.subject === s.value;
								return (
									<button
										key={s.value}
										type="button"
										onClick={() => update('subject', s.value)}
										className={cn(
											'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-medium transition-all whitespace-nowrap active:scale-95 border',
											active
												? 'bg-white/[0.12] border-white/[0.18] text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
												: 'bg-white/[0.05] border-white/[0.08] text-foreground hover:bg-white/[0.08]'
										)}
									>
										<span className="text-[13px] leading-none">{s.emoji}</span>
										{s.label}
										{active && <Check className="h-3 w-3 ml-0.5 text-active-gold" strokeWidth={3} />}
									</button>
								);
							})}
					</div>
				</div>
				{errors.subject && (
					<p className="text-[11px] text-destructive mt-1">{errors.subject}</p>
				)}
			</div>

			{/* Composer */}
			<form
				onSubmit={handleSubmit}
				className="px-3 py-2 border-t border-white/[0.06] bg-background/95 backdrop-blur-xl shrink-0"
			>
				<input
					type="text"
					name="website"
					value={honeypot}
					onChange={(e) => setHoneypot(e.target.value)}
					style={{ display: 'none' }}
					tabIndex={-1}
					autoComplete="off"
					aria-hidden
				/>

				{allowsAttachments && attachments.length > 0 && (
					<div className="flex gap-2 mb-2 px-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
						{attachments.map((f, i) => (
							<div
								key={i}
								className="relative h-14 w-14 shrink-0 rounded-lg overflow-hidden border border-white/[0.08] bg-white/[0.04]"
							>
								<img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" />
								<button
									type="button"
									onClick={() => removeAttachment(i)}
									aria-label="Remove screenshot"
									className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-black/80 border border-white/20 grid place-items-center text-foreground"
								>
									<X className="h-3 w-3" strokeWidth={2.5} />
								</button>
							</div>
						))}
					</div>
				)}

				{formData.subject && (
					<div className="grid grid-cols-2 gap-2 mb-2 px-0.5 animate-fade-in">
						<input
							type="text"
							id="contact-name"
							name="name"
							value={formData.name}
							onChange={(e) => update('name', e.target.value)}
							disabled={isSubmitting}
							placeholder="Your name"
							autoComplete="name"
							required
							aria-label="Your name"
							className={cn(
								'w-full h-10 px-4 rounded-[22px] bg-white/[0.05] border border-white/[0.08] text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/60 transition-all',
								errors.name && 'border-destructive'
							)}
						/>
						<input
							type="email"
							id="contact-email"
							name="email"
							value={formData.email}
							onChange={(e) => update('email', e.target.value)}
							onBlur={handleEmailBlur}
							disabled={isSubmitting}
							placeholder="you@email.com"
							autoComplete="email"
							inputMode="email"
							required
							aria-label="Email"
							className={cn(
								'w-full h-10 px-4 rounded-[22px] bg-white/[0.05] border border-white/[0.08] text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/60 transition-all',
								errors.email && 'border-destructive'
							)}
						/>
						{(errors.name || errors.email) && (
							<p className="col-span-2 text-[11px] text-destructive">
								{errors.name || errors.email}
							</p>
						)}
					</div>
				)}



				<div
					className={cn(
						'flex items-end gap-1 rounded-[22px] bg-white/[0.05] border border-white/[0.08] pl-1.5 pr-1.5 py-1.5 transition-all',
						errors.message && 'border-destructive'
					)}
				>
					{allowsAttachments && (
						<>
							<input
								ref={fileInputRef}
								type="file"
								accept={IMAGE_ACCEPT}
								multiple
								className="hidden"
								onChange={(e) => addAttachments(e.target.files, fileInputRef.current)}
							/>
							<button
								type="button"
								onClick={() => fileInputRef.current?.click()}
								disabled={isSubmitting || attachments.length >= MAX_ATTACHMENTS}
								aria-label="Add screenshot"
								className="h-10 w-10 shrink-0 self-start rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] disabled:opacity-40 transition-colors"
							>
								<Paperclip className="h-5 w-5" />
							</button>
						</>
					)}
					<textarea
						ref={messageRef}
						value={formData.message}
						onChange={(e) => update('message', e.target.value)}
						disabled={isSubmitting || !formData.subject}
						readOnly={!formData.subject}
						rows={1}
						maxLength={2000}
						placeholder={formData.subject ? 'Write a message…' : 'Pick a topic above to start'}
						className={cn(
							'flex-1 bg-transparent text-[16px] text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none py-2 leading-snug max-h-[200px]',
							allowsAttachments ? 'pl-1' : 'pl-2.5'
						)}
					/>
					<SendButton canSend={canSend} isSubmitting={isSubmitting} pct={charPct} />
				</div>
				{errors.message && (
					<p className="text-[11px] text-destructive mt-1.5 px-2">{errors.message}</p>
				)}
				{formData.message.length > 1600 && (
					<p
						className={cn(
							'text-[10px] tabular-nums text-right mt-1 px-2',
							formData.message.length >= 2000 ? 'text-destructive' : 'text-warning'
						)}
					>
						{formData.message.length} / 2000
					</p>
				)}
			</form>
		</div>
	);
};

export default ContactMessager;
